import React, { useEffect, useState } from 'react';
import Button from '../../../components/button';
import Table from '../../../components/table';
import Heading from '../../../components/typography';
import InitialValuesForm from '../../../constants/initialValues';
import { ModalStatusType, TableParamsType } from '../../../types/common';
import {
  SettingSystemUserButton,
  SettingSystemUserHeader,
  SystemUserContainer,
} from '../system-users/systemUserStyled';
import {
  useAdvanceBoolean,
  useBoolean,
  useDeleteFeatureSet,
  useGetFeatureSetById,
  useGetVendorFeatureSet,
  useWealthlaneIdentityApi,
} from '../../../hooks';
import { VendorFeatureSetHeader } from '../../../constants/table-headers/vendorFeatureSetHeader';
import { PageHeader } from '../../../constants/style/common.styled';
import Search from '../../../components/search/search';
import { SettingRoleButton } from '../system-roles/systemRoleStyled';
import AnimationWrapper from '../../../components/animation/animationWrapper';
import FeatureSetModal from './featureSetModal';
import FeatureSetDeleteConfirmModal from './featureSetDeleteConfirmModal';
import permissionCheck from '../../../utils/permission/permissionCheck';
import { FeatureSetService } from '@wealthlane/api-services/wealthlane-identity-service';
import { getFieldLevelPermissions, getNonFieldLevelPermissions } from '../../../utils';
import {
  DynamicTable,
  withDynamicTableContext,
  useDynamicTable,
  useTableSchemaLoader,
} from '@wealthlane/table-builder';
import Spinners from '../../../components/spinner';
import { axiosInstance } from '../../../services/apiInterceptor';
import ManageFeatureSetModal from '../permission-management/manageFeatureSetModal';
import FilterFeatureSetModal from './filterFeatureSetModal';
import { Wealthlane_AppServices_Features_DeleteFeatureSetResponseDto } from '@wealthlane/api-services/wealthlane-identity-service';
const featureSetState = {
  data: InitialValuesForm.featureSetModal,
  id: '',
};

interface FeatureSetProps {
  hasId?: string;
}
const FeatureSet = ({ hasId }: FeatureSetProps): JSX.Element => {
  const hasDeletePermission = permissionCheck({
    type: 'Delete',
    permission: 'FeatureSet',
    feature: 'Vendor',
  });
  const { handleRequest } = useWealthlaneIdentityApi();
  // const [tableInfo, setTableInfo] = useState<TableParamsType>(InitialValuesForm.tableParams);
  const { tableInfo, setTableInfo } = useDynamicTable();
  useEffect(() => {
    setTableInfo(InitialValuesForm.tableParams);
  }, []);
  const {
    value: isFilterModalOpen,
    setFalse: closeFilterModal,
    setTrue: openFilterModal,
    customValue: filterCustomValue,
    setCustomValue: setFilterCustomValue,
  } = useAdvanceBoolean<string>(false);
  const {
    data: featureSetList,
    isLoading,
    status,
  } = useGetVendorFeatureSet(handleRequest, tableInfo, filterCustomValue);
  const [searchQuery, setSearchQuery] = useState('');
  const { value: isModalOpen, setFalse: closeModal, setTrue: openModal } = useBoolean(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);

  const {
    value: isManageModalOpen,
    setFalse: closeManageModal,
    setTrue: openManageModal,
  } = useBoolean(false);
  const [modalStatus, setModalStatus] = useState<ModalStatusType>('Add');
  const [loading, setLoading] = useState(false);
  const [featureSetRolesInitialData, setFeatureSetRolesInitialData] = useState(featureSetState);

  const {
    value: isDeleteConfirmModalOpen,
    setFalse: closeDeleteConfirmModal,
    setTrue: openDeleteConfirmModal,
  } = useBoolean(false);
  const [deleteTargetId, setDeleteTargetId] = useState('');
  const [deleteMessage, setDeleteMessage] = useState('');
  const [deleteIsAssigned, setDeleteIsAssigned] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const { mutate: deleteFeatureSet, isLoading: isDeleting } = useDeleteFeatureSet(handleRequest, closeDeleteConfirmModal);
  const { schemaData, isTableSchemaLoading } = useTableSchemaLoader({
    identifier: 'FEATURE_SET_TABLE',
    baseUrl: process.env.REACT_APP_WEALTHLANE_API_ENDPOINT,
  });
  const data = React.useMemo(() => featureSetList?.items || [], [featureSetList, isLoading]);

  const getFeatureSetRoleData = (id: string, isDefaultAdmin?: boolean): void => {
    setLoading(true);
    handleRequest(FeatureSetService.getApiIdentityFeatureSet1(id))
      .then((data: any) => {
        if (data != null) {
          const filteredData = {
            ...data,
            permissions: (data.permissions as boolean)
              ? getNonFieldLevelPermissions(data.permissions).reduce(
                (a: any, v: any) => ({ ...a, [v.replaceAll('.', '-')]: true }),
                {}
              )
              : {},
            fieldLevelPermissions: (data.permissions as boolean)
              ? getFieldLevelPermissions(data.permissions).reduce(
                (a: any, v: any) => ({ ...a, [v.replaceAll('.', '-')]: true }),
                {}
              )
              : {},
          };

          setFeatureSetRolesInitialData({ id, data: filteredData });
          setLoading(false);
        }
      })
      .catch((error: any) => {
        console.log('error', error);
        setLoading(false);
      });
  };

  const [checkedValues, setCheckedValues] = useState<any>([]);
  const handleChecked = (values: string[]): void => {
    setCheckedValues(values);
  };

  useEffect(() => {
    if (checkedValues.length > 0) {
      setSelectedRowData(data.filter((row: any) => checkedValues.includes(row.id)));
    } else {
      setSelectedRowData(null);
    }
  }, [checkedValues]);

  const handleDeletePreview = (id: string): void => {
    setDeleteLoading(true);
    handleRequest(FeatureSetService.deleteApiIdentityFeatureSet(id, false))
      .then((response: Wealthlane_AppServices_Features_DeleteFeatureSetResponseDto | undefined) => {
        if (response != null && response.success) {
          setDeleteTargetId(id);
          setDeleteMessage(response.message ?? 'Are you sure you want to delete this feature set?');
          setDeleteIsAssigned(response.isAssigned ?? false);
          openDeleteConfirmModal();
        }
      })
      .catch(() => { })
      .finally(() => {
        setDeleteLoading(false);
      });
  };

  const handleDeleteConfirm = (): void => {
    deleteFeatureSet(deleteTargetId);
  };

  const actions = [
    {
      action: 'View',
      onClick: (id: string, rowData: any) => {
        getFeatureSetRoleData(id, rowData);
        openModal();
        setModalStatus('View');
      },
      id: 'viewFeatureSetAction',
    },
    {
      action: 'Edit',
      as: 'Edit',
      onClick: (id: string, rowData: any) => {
        getFeatureSetRoleData(id, rowData);
        openModal();
        setModalStatus('Edit');
      },
      id: 'editFeatureSetAction',
    },
    {
      action: <div className='delete-label'>Delete</div>,
      as: 'Delete',
      onClick: (id: string) => {
        handleDeletePreview(id);
      },
      isHidden: (rowData: any) => !hasDeletePermission || !rowData?.isEditable || rowData?.isDefault,
      id: 'deleteFeatureSetAction',
    },
  ];
  const renderRightToolbarButton = () => {
    return (
      <div className='flex gap-4'>
        <Button
          as='Create'
          variant={'primary'}
          title={'Add Feature Set'}
          icon={'add_circle_outline'}
          id='addFeatureSetButton'
          onClick={() => {
            setFeatureSetRolesInitialData(featureSetState);
            setModalStatus('Add');
            // setDataRow('');
            openModal();
          }}
        />
        <Button
          as='Create'
          variant={'primary-outline'}
          title={'Bulk Edit'}
          icon={'discover_tune'}
          hasId={hasId}
          id='bulkEditFeatureSetButton'
          onClick={() => {
            setFeatureSetRolesInitialData(featureSetState);
            // setDataRow('');
            openManageModal();
          }}
          disabled={checkedValues?.length === 0}
        />
      </div>
    );
  };

  const schema = React.useMemo(
    () => ({
      columns: VendorFeatureSetHeader({ actions: schemaData?.columns, tableActions: actions }),
      options: schemaData?.options as any,
    }),
    [schemaData]
  );

  if (isTableSchemaLoading) return <Spinners containerClassName='h-screen' />;

  return (
    <>
      <PageHeader data-page='featureSet' id='pageContainer'>
        <Heading variant='h3' title='Feature Set' id='pageTitle' />
        <Heading
          variant='body-s'
          title={`Current list of Wealthlane system Feature Set.`}
          id='pageDescription'
        />
      </PageHeader>
      {/* <div className='grid sm:grid-cols-12 justify-between gap-4 sm:gap-0'>
        <div className='lg:col-span-3 col-span-6'>
          <Search
            placeholder='Search'
            searchValue={searchQuery}
            onSearch={(value: string) => {
              setSearchQuery(value);
            }}
            setTableInfo={setTableInfo}
            tableInfo={tableInfo}
          />
        </div>
        <SettingRoleButton className='lg:col-start-10 lg:col-span-3 col-span-6'>
          <Button
            as='Create'
            variant={'primary'}
            title={'Add Feature Set'}
            icon={'add_circle_outline'}
            onClick={() => {
              setFeatureSetRolesInitialData(featureSetState);
              setModalStatus('Add');
              // setDataRow('');
              openModal();
            }}
          />
        </SettingRoleButton>
      </div> */}
      <AnimationWrapper>
        {isModalOpen && (
          <FeatureSetModal
            id='featureSetModal'
            isModalOpen={isModalOpen}
            closeModal={closeModal}
            initialData={featureSetRolesInitialData}
            handleRequest={handleRequest}
            modalStatus={modalStatus}
            setModalStatus={setModalStatus}
          />
        )}
      </AnimationWrapper>
      <AnimationWrapper>
        {isManageModalOpen && (
          <ManageFeatureSetModal
            id='manageFeatureSetModal'
            isModalOpen={isManageModalOpen}
            closeModal={closeManageModal}
            checkedValues={checkedValues}
            setCheckedValues={setCheckedValues}
            handleRequest={handleRequest}
            permissionType='feature-set'
            selectedRowData={selectedRowData}
          />
        )}
      </AnimationWrapper>
      <AnimationWrapper>
        {isFilterModalOpen && (
          <FilterFeatureSetModal
            id='featureSetModal'
            isModalOpen={isFilterModalOpen}
            closeModal={closeFilterModal}
            initialData={featureSetState}
            handleRequest={handleRequest}
            modalStatus={'Edit'}
            setModalStatus={setModalStatus}
            setFilterCustomValue={setFilterCustomValue}
          />
        )}
      </AnimationWrapper>
      <AnimationWrapper>
        {isDeleteConfirmModalOpen && (
          <FeatureSetDeleteConfirmModal
            isShow={isDeleteConfirmModalOpen}
            handleClose={closeDeleteConfirmModal}
            handleSubmit={handleDeleteConfirm}
            message={deleteMessage}
            isAssigned={deleteIsAssigned}
            loading={isDeleting}
          />
        )}
      </AnimationWrapper>

      {/* <Table
        columns={VendorFeatureSetHeader(actions)}
        data={featureSetList?.items ?? []}
        loading={status}
        showPagination
        totalCounts={featureSetList?.totalCount ?? 10}
        tableInfo={tableInfo}
        setTableInfo={setTableInfo}
      /> */}
      <DynamicTable
        id='featureSetTable'
        schema={schema}
        data={data}
        totalCounts={featureSetList?.totalCount ?? 10}
        loading={status}
        components={{
          renderRightToolbarButton,
        }}
        onFilterClick={(isResetFilter: boolean) => {
          if (isResetFilter) {
            setFilterCustomValue([]);
            setTableInfo({ ...tableInfo });
          } else {
            openFilterModal();
          }
        }}
        onChecked={handleChecked}
        checkedValues={checkedValues}
        showCheckBox={true}
      />
    </>
  );
};

export default withDynamicTableContext(FeatureSet, false, axiosInstance);
